import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {User} from '@cp/model';
import subscribeUserDoc from '../../api/user/subscribeUserDoc';
import updateUserDoc from '../../api/user/updateUserDoc';
import useDownloadStorageFile from '../helper/useDownloadStorageFile';
import {useFunctionSubscribe} from '../helper/function/useFunctionSubscribe';
import {useEffect, useMemo} from 'react';

dayjs.extend(utc);

interface UseGetUserOptions {
  skip?: boolean;
  uid?: string;
}

export function useGetUser(options?: UseGetUserOptions) {
  const {skip = false, uid} = options ?? {};
  const shouldSubscribe = !skip && Boolean(uid);

  const {
    data: user,
    loading,
    error,
  } = useFunctionSubscribe<User | null>(
    (onData, onError) =>
      subscribeUserDoc(uid!, onData, (err) => {
        console.error('useGetUser: error reading user document', err);
        onError(err);
      }),
    {skip: !shouldSubscribe, deps: [uid]},
  );

  const {url: processedPhotoUrl} = useDownloadStorageFile(user?.photoUrl || undefined);
  const {url: processedBackgroundUrl} = useDownloadStorageFile(user?.backgroundImageUrl || undefined);
  const {url: processedClickSoundUrl} = useDownloadStorageFile(user?.clickSoundUrl || undefined);
  const {url: processedCheckSoundUrl} = useDownloadStorageFile(user?.checkSoundUrl || undefined);
  const {url: processedDropdownSoundUrl} = useDownloadStorageFile(user?.dropdownSoundUrl || undefined);
  const {url: processedSwitchSoundUrl} = useDownloadStorageFile(user?.switchSoundUrl || undefined);
  const {url: processedSlideSoundUrl} = useDownloadStorageFile(user?.slideSoundUrl || undefined);

  const processedUser = useMemo(
    () =>
      user != null
        ? {
            ...user,
            photoUrl: processedPhotoUrl,
            backgroundImageUrl: processedBackgroundUrl,
            soundVolume: user.soundVolume !== null && user.soundVolume !== undefined ? user.soundVolume : undefined,
            clickSoundUrl: processedClickSoundUrl,
            checkSoundUrl: processedCheckSoundUrl,
            dropdownSoundUrl: processedDropdownSoundUrl,
            switchSoundUrl: processedSwitchSoundUrl,
            slideSoundUrl: processedSlideSoundUrl,
          }
        : null,
    [
      user,
      processedPhotoUrl,
      processedBackgroundUrl,
      processedClickSoundUrl,
      processedCheckSoundUrl,
      processedDropdownSoundUrl,
      processedSwitchSoundUrl,
      processedSlideSoundUrl,
    ],
  );

  // Update lastLogin if the stored date is not today (UTC)
  useEffect(() => {
    if (!uid || processedUser == null) return;
    const lastLogin = processedUser.lastLogin;
    if (!lastLogin) return;
    const lastLoginDate = lastLogin instanceof Date ? lastLogin : new Date(lastLogin as unknown as string);
    const isTodayUtc = dayjs.utc(lastLoginDate).isSame(dayjs.utc(), 'day');
    if (!isTodayUtc) {
      updateUserDoc(uid, {lastLogin: new Date()});
    }
  }, [uid, processedUser]);

  return {
    user: processedUser,
    loading,
    error: error ?? null,
    refetch: () => {},
  };
}
